// Business partners (suppliers now, customers with Sales) — data access.
//
//   list    business_partners?select=<list cols>&is_supplier=eq.true&code=gt.<cursor>&order=code&limit=51
//           index: business_partners_supplier_code_idx (tenant_id, code) where is_supplier
//   search  rpc search_business_partners(term, role, inactive, after_code, limit) — trigram GIN
//           business_partners_search_idx; SECURITY DEFINER because a user's LIKE is not
//           leakproof under RLS (same contract as search_items)
//   detail  one request: partner + addresses (embedded)
//   writes  plain insert/update/delete; RLS + column grants enforce the permission
//           (master.partner.manage) and the immutable code; triggers write the audit log.

import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";
import { isDemoMode, supabase } from "@/lib/supabase";
import { demoApi } from "./demo";
import { sortParam, type ListFilter, type ListSort } from "@patronix/domain/list";

export type PartnerRole = "supplier" | "customer";
/** A list shows one role, or every partner (Master data → Business partners). */
export type PartnerListRole = PartnerRole | "all";
export const addressKinds = ["main", "ship_from", "ship_to"] as const;
export type AddressKind = (typeof addressKinds)[number];

export interface PartnerRow {
  id: string;
  code: string;
  name: string;
  tax_id: string | null;
  country: string | null;
  currency: string | null;
  is_supplier: boolean;
  is_customer: boolean;
  is_active: boolean;
  supplier_lead_time_days?: number | null;
  /** Customers: payment terms text (not returned by the search RPC). */
  customer_payment_terms?: string | null;
}

export interface PartnerAddress {
  id: string;
  kind: AddressKind;
  name: string | null;
  line1: string;
  line2: string | null;
  postal_code: string | null;
  city: string;
  country: string;
  is_default: boolean;
}

export interface PartnerDetail extends PartnerRow {
  email: string | null;
  phone: string | null;
  supplier_lead_time_days: number | null;
  supplier_receipt_tolerance_pct: number | string;
  customer_payment_terms: string | null;
  note: string | null;
  updated_at: string;
  addresses: PartnerAddress[];
}

export interface PartnerInput {
  code: string;
  name: string;
  tax_id: string | null;
  country: string | null;
  email: string | null;
  phone: string | null;
  currency: string | null;
  is_supplier: boolean;
  is_customer: boolean;
  supplier_lead_time_days: number | null;
  supplier_receipt_tolerance_pct: string;
  customer_payment_terms: string | null;
  note: string | null;
  is_active: boolean;
}

export type AddressInput = Omit<PartnerAddress, "id">;

export interface PartnerListParams {
  role: PartnerListRole;
  q: string;
  inactive: boolean;
  /** Column filters and sort (partner_page spec, partnerListColumns). */
  filters?: ListFilter[];
  sort?: ListSort | null;
}

export interface PartnerPage {
  rows: PartnerRow[];
  nextCursor: string | null;
}

export const PARTNER_PAGE = 50;

const detailColumns =
  "id,code,name,tax_id,country,email,phone,currency,is_supplier,is_customer,supplier_lead_time_days," +
  "supplier_receipt_tolerance_pct,customer_payment_terms,note,is_active,updated_at," +
  "addresses:business_partner_addresses!business_partner_addresses_partner_fkey(id,kind,name,line1,line2,postal_code,city,country,is_default)";

function db() {
  if (!supabase) throw new Error("Supabase is not configured");
  return supabase;
}

export async function listPartners(p: PartnerListParams, cursor: string | null, signal: AbortSignal, size = PARTNER_PAGE): Promise<PartnerPage> {
  if (isDemoMode) return demoApi.list(p, cursor);
  // One RPC for list, search, column filters and sort (partner_page).
  const { data, error } = await db()
    .rpc("partner_page", {
      p_role: p.role === "all" ? null : p.role,
      p_term: p.q.trim() || null,
      p_include_inactive: p.inactive,
      p_filters: p.filters ?? [],
      p_sort: sortParam(p.sort ?? null),
      p_after: cursor ? JSON.parse(cursor) : null,
      p_limit: size + 1,
    })
    .abortSignal(signal);
  if (error) throw error;
  const rows = data as (PartnerRow & { sort_cursor: unknown })[];
  const hasMore = rows.length > size;
  const slice = hasMore ? rows.slice(0, size) : rows;
  const last = slice[slice.length - 1];
  return { rows: slice, nextCursor: hasMore && last ? JSON.stringify(last.sort_cursor) : null };
}

async function getPartner(id: string, signal: AbortSignal): Promise<PartnerDetail | null> {
  if (isDemoMode) return demoApi.get(id);
  const { data, error } = await db()
    .from("business_partners")
    .select(detailColumns)
    .eq("id", id)
    .abortSignal(signal)
    .maybeSingle();
  if (error) throw error;
  return data as unknown as PartnerDetail | null;
}

export async function createPartner(tenantId: string, input: PartnerInput): Promise<string> {
  if (isDemoMode) return demoApi.create(input);
  const { data, error } = await db()
    .from("business_partners")
    .insert({ tenant_id: tenantId, ...input })
    .select("id")
    .single();
  if (error) throw error;
  return (data as { id: string }).id;
}

export async function updatePartner(id: string, input: Omit<PartnerInput, "code">): Promise<void> {
  if (isDemoMode) return demoApi.update(id, input);
  const { error } = await db().from("business_partners").update(input).eq("id", id);
  if (error) throw error;
}

export async function addPartnerAddress(tenantId: string, partnerId: string, input: AddressInput): Promise<void> {
  if (isDemoMode) return demoApi.addAddress(partnerId, input);
  const { error } = await db()
    .from("business_partner_addresses")
    .insert({ tenant_id: tenantId, partner_id: partnerId, ...input });
  if (error) throw error;
}

export async function deletePartnerAddress(id: string): Promise<void> {
  if (isDemoMode) return demoApi.deleteAddress(id);
  const { error } = await db().from("business_partner_addresses").delete().eq("id", id);
  if (error) throw error;
}

// ---------------------------------------------------------------------------
// Query options
// ---------------------------------------------------------------------------

export const partnerKeys = {
  all: ["master-data", "partners"] as const,
  list: (p: PartnerListParams) => [...partnerKeys.all, "list", p] as const,
  detail: (id: string) => [...partnerKeys.all, "detail", id] as const,
};

export function partnerListQuery(p: PartnerListParams) {
  return infiniteQueryOptions({
    queryKey: partnerKeys.list(p),
    queryFn: ({ pageParam, signal }) => listPartners(p, pageParam, signal),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    staleTime: 60_000,
  });
}

export function partnerDetailQuery(id: string) {
  return queryOptions({
    queryKey: partnerKeys.detail(id),
    queryFn: ({ signal }) => getPartner(id, signal),
    staleTime: 60_000,
  });
}
